import type { SearchSelectOption } from "@components/FormsV2/SearchSelect";
import type { siteLinkPayload } from "@components/ClientRecordPicker/siteLink";

// A JSON:API record as the v2 hooks return it.
export type ApiRecord = {
  id?: string;
  type?: string;
  attributes?: any;
  relationships?: any;
};

export interface VariableValue {
  name: string;
  value: string;
}

// What a picked client location / ConnectWise site adds to the location write.
export type SiteLink = ReturnType<typeof siteLinkPayload>;

export interface ProjectAddress {
  name: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  countryId: string;
  remote: boolean;
}

export interface NewProjectLocation extends ProjectAddress {
  projectVariables: VariableValue[];
  link: SiteLink | null;
}

export interface NewProjectValues {
  projectName: string;
  // id null: find the client by name, or create it.
  client: { id: string | null; name: string };
  salesExecutive: { id: string | null; name: string } | null;
  crmOpportunityId: string | null;
  presalesEngineerId: string | null;
  paymentTermId: string | null;
  rateTableId: string | null;
  businessUnitId: string | null;
  msaDate: string | null;
  tags: string[];
  projectVariables: VariableValue[];
  // null: no location entered, or the saved one is unchanged.
  location: NewProjectLocation | null;
}

// A saved project the form starts on (the edit page). The selects whose saved
// record may no longer be listed (inactive) carry their label too.
export interface ProjectFormInitialValues {
  projectName: string;
  client: { id: string | null; name: string };
  crmOpportunity: SearchSelectOption | null;
  salesExecutive: SearchSelectOption | null;
  presalesEngineer: SearchSelectOption | null;
  paymentTermId: string | null;
  rateTable: SearchSelectOption | null;
  businessUnitId: string | null;
  msaDate: string | null;
  tags: string[];
  projectVariables: VariableValue[];
  // null: the project has no location yet.
  location: NewProjectLocation | null;
}

export type ProjectSaveStage = "client" | "project" | "location";

export class ProjectSaveError extends Error {
  stage: ProjectSaveStage;

  constructor(stage: ProjectSaveStage, message: string) {
    super(message);
    // Keeps `instanceof` working when TS compiles classes to ES5.
    Object.setPrototypeOf(this, ProjectSaveError.prototype);
    this.name = "ProjectSaveError";
    this.stage = stage;
  }
}
